import { useId, type ReactNode } from 'react';
import { CaretDown, DotsThree, MagnifyingGlass, X, type Icon as PhosphorIcon } from '@phosphor-icons/react';
import { cx } from '../../lib/cx';
import { Icon } from '../Icon';

const PILL_BASE =
  'inline-flex shrink-0 items-center gap-(--mbm-space-1-5) whitespace-nowrap rounded-full border text-label-medium ' +
  'focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)';

/**
 * `md` is the 34px pill of the directory tables (Figma A03.1, A04). `lg` is the 38px one of a business, project or
 * person page: its header actions and the toolbar over its list (Figma A05.1, A05.2, A04.2).
 */
export type PillSize = 'md' | 'lg';
const PILL_SIZE: Record<PillSize, string> = {
  md: 'h-(--mbm-size-toolbar-control) px-(--mbm-space-3)',
  lg: 'h-(--mbm-size-toolbar-control-lg) px-(--mbm-space-3-5)',
};

// Each tone carries all of its colours, so two tones never fight over the same property.
const PILL_TONE = {
  light: 'border-border-input bg-surface-card text-text-primary',
  /** A filter that currently has a value. */
  active: 'border-action-primary bg-surface-card text-text-primary',
  /** The page's primary action ("Add staff"). */
  dark: 'border-border-input bg-action-primary text-text-on-dark',
} as const;

/** Every control in a staff toolbar or page header is the same pill; only its tone (and, per page, its size) changes. */
export const pillClass = (tone: keyof typeof PILL_TONE = 'light', size: PillSize = 'md') =>
  cx(PILL_BASE, PILL_SIZE[size], PILL_TONE[tone]);

/** The toolbar's search: an outlined 34px pill (not the grey client-side search field). */
export function ToolbarSearch({
  value,
  onChange,
  placeholder,
  'aria-label': ariaLabel,
  filled = false,
  size = 'md',
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  'aria-label': string;
  /** Grey, borderless: the project and task lists' search (Figma A05.1, A05.2). */
  filled?: boolean;
  size?: PillSize;
  className?: string;
}) {
  return (
    <div
      className={cx(
        'flex items-center gap-(--mbm-space-2) rounded-full',
        size === 'lg'
          ? 'h-(--mbm-size-toolbar-control-lg) px-(--mbm-space-3-5)'
          : 'h-(--mbm-size-toolbar-control) px-(--mbm-space-3)',
        filled ? 'bg-surface-field' : 'border border-border-input bg-surface-card',
        'focus-within:shadow-(--mbm-shadow-focus-ring)',
        className,
      )}
    >
      <Icon icon={MagnifyingGlass} size="sm" />
      <input
        type="search"
        value={value}
        aria-label={ariaLabel}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="h-full min-w-0 flex-1 bg-transparent text-body-small text-text-primary outline-none placeholder:text-text-tertiary [&::-webkit-search-cancel-button]:hidden"
      />
      {value ? (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => onChange('')}
          className="text-text-muted hover:text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
        >
          <Icon icon={X} size="xs" />
        </button>
      ) : null}
    </div>
  );
}

/**
 * A filter that works: a pill with the filter's name and a caret, over a native <select> (so the platform picker,
 * keyboard and screen-reader behaviour all come for free). The pill shows what is chosen once something is.
 */
export function FilterSelectPill({
  label,
  value,
  onChange,
  options,
  showAll = false,
  allText = 'All',
  bare = false,
  size = 'md',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  /** Name the unfiltered state ("Domain: All") instead of just the filter. */
  showAll?: boolean;
  /** What the unfiltered state is called: "All" for a category, "Anyone" for a person. */
  allText?: string;
  /** The unfiltered pill says only `allText` ("All pods"), not "Pod: All pods". */
  bare?: boolean;
  size?: PillSize;
}) {
  const id = useId();
  const chosen = options.find((o) => o.value === value && value !== '');
  return (
    <span className="relative inline-flex">
      <span className={cx(pillClass(chosen ? 'active' : 'light', size === 'lg' ? 'lg' : 'md'), 'pointer-events-none')}>
        {chosen ? chosen.label : bare ? allText : showAll ? `${label}: ${allText}` : label}
        <Icon icon={CaretDown} size="xs" />
      </span>
      <select
        id={id}
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 size-full cursor-pointer opacity-0"
      >
        <option value="">{bare ? allText : `${label}: ${allText}`}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </span>
  );
}

/**
 * A control the design shows but the product cannot back yet (plan and billing filters, export): rendered exactly as
 * designed and inert, answering "coming soon" instead of doing nothing silently.
 */
export function PillButton({
  label,
  onClick,
  caret = true,
  icon,
  dark = false,
  size = 'md',
  children,
}: {
  label: string;
  onClick: () => void;
  caret?: boolean;
  icon?: PhosphorIcon;
  /** The page's primary action ("Add staff"): the same 34px pill, filled dark. */
  dark?: boolean;
  size?: PillSize;
  children?: ReactNode;
}) {
  return (
    <button type="button" onClick={onClick} className={pillClass(dark ? 'dark' : 'light', size)} aria-label={label}>
      {icon ? <Icon icon={icon} size="sm" /> : null}
      {children ?? label}
      {caret ? <Icon icon={CaretDown} size="xs" /> : null}
    </button>
  );
}

/** The round "⋯" at the end of a page header's actions (Figma A05.1, A05.2, A04.2): a 38px outlined circle. */
export function MoreButton({ label = 'More actions', onClick }: { label?: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="inline-flex size-(--mbm-size-toolbar-control-lg) shrink-0 items-center justify-center rounded-full border border-border-input bg-surface-card text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
    >
      <Icon icon={DotsThree} size="md" />
    </button>
  );
}
